import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { supabase } from '../lib/supabase';

const WaitlistModal = ({ isOpen, onClose }) => {
  const { isMobile } = useBreakpoint();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    console.log('🚀 Starting waitlist submission...');
    console.log('📝 Form data:', formData);
    console.log('🔗 Supabase URL:', supabase.supabaseUrl);
    console.log('📊 Table name: blank_waitlist');

    try {
      // Insert into Supabase
      console.log('📤 Sending insert request to Supabase...');
      const { data, error: supabaseError } = await supabase
        .from('blank_waitlist')
        .insert([
          {
            name: formData.name,
            email: formData.email,
            created_at: new Date().toISOString(),
          },
        ])
        .select();

      console.log('📥 Supabase response received');
      console.log('✅ Data:', data);
      console.log('❌ Error:', supabaseError);

      if (supabaseError) {
        console.error('🚨 Supabase error details:', {
          code: supabaseError.code,
          message: supabaseError.message,
          details: supabaseError.details,
          hint: supabaseError.hint,
        });

        // Check if it's a duplicate email error
        if (supabaseError.code === '23505') {
          setError('This email is already on the waitlist.');
        } else {
          throw supabaseError;
        }
        setIsSubmitting(false);
        return;
      }

      console.log('✨ Waitlist submission successful!', data);
      setIsSuccess(true);

      // Reset form after 2 seconds
      setTimeout(() => {
        setIsSuccess(false);
        setFormData({ name: '', email: '' });
        setError(null);
        onClose();
      }, 2000);
    } catch (err) {
      console.error('💥 Caught error:', err);
      console.error('💥 Error details:', {
        name: err.name,
        message: err.message,
        code: err.code,
        stack: err.stack,
      });
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
      console.log('🏁 Submission process complete');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(5, 5, 5, 0.9)',
              zIndex: 9998,
              backdropFilter: 'blur(8px)',
            }}
          />

          {/* Modal */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999,
              padding: 16,
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.5, bounce: 0.3 }}
              style={{
                width: '100%',
                maxWidth: 500,
              }}
            >
            <div
              style={{
                background: '#050505',
                border: '1px solid #F2F2F2',
                padding: isMobile ? '32px 20px' : 40,
                position: 'relative',
              }}
            >
              {/* Close Button */}
              <button
                data-hover
                onClick={onClose}
                style={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  background: 'transparent',
                  border: 'none',
                  color: '#F2F2F2',
                  fontSize: 24,
                  cursor: 'none',
                  padding: 8,
                  lineHeight: 1,
                }}
              >
                ×
              </button>

              {!isSuccess ? (
                <>
                  {/* Header */}
                  <div style={{ marginBottom: 32 }}>
                    <p
                      className="font-mono"
                      style={{
                        fontSize: 10,
                        color: '#00FF41',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3em',
                        marginBottom: 12,
                      }}
                    >
                      JOIN_WAITLIST
                    </p>
                    <h2
                      style={{
                        fontSize: 28,
                        fontWeight: 600,
                        letterSpacing: '-0.02em',
                        marginBottom: 12,
                      }}
                    >
                      Go Blank.
                    </h2>
                    <p
                      className="font-mono"
                      style={{
                        fontSize: 11,
                        color: 'rgba(242,242,242,0.6)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        lineHeight: 1.6,
                      }}
                    >
                      Get early access to the protocol. No spam. No tracking.
                    </p>
                  </div>

                  {/* Form */}
                  <form onSubmit={handleSubmit}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {/* Name Input */}
                      <div>
                        <label
                          className="font-mono"
                          htmlFor="name"
                          style={{
                            display: 'block',
                            fontSize: 10,
                            color: '#F2F2F2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            marginBottom: 8,
                          }}
                        >
                          Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Enter your name"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'transparent',
                            border: '1px solid rgba(242,242,242,0.3)',
                            color: '#F2F2F2',
                            fontSize: 14,
                            fontFamily: 'Inter, sans-serif',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#00FF41')}
                          onBlur={(e) => (e.target.style.borderColor = 'rgba(242,242,242,0.3)')}
                        />
                      </div>

                      {/* Email Input */}
                      <div>
                        <label
                          className="font-mono"
                          htmlFor="email"
                          style={{
                            display: 'block',
                            fontSize: 10,
                            color: '#F2F2F2',
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                            marginBottom: 8,
                          }}
                        >
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            background: 'transparent',
                            border: '1px solid rgba(242,242,242,0.3)',
                            color: '#F2F2F2',
                            fontSize: 14,
                            fontFamily: 'Inter, sans-serif',
                            outline: 'none',
                            transition: 'border-color 0.2s',
                          }}
                          onFocus={(e) => (e.target.style.borderColor = '#00FF41')}
                          onBlur={(e) => (e.target.style.borderColor = 'rgba(242,242,242,0.3)')}
                        />
                      </div>

                      {/* Error Message */}
                      {error && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          style={{
                            padding: '12px 16px',
                            background: 'rgba(255, 0, 0, 0.1)',
                            border: '1px solid rgba(255, 0, 0, 0.3)',
                            borderRadius: 4,
                          }}
                        >
                          <p
                            className="font-mono"
                            style={{
                              fontSize: 11,
                              color: '#FF4444',
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                            }}
                          >
                            {error}
                          </p>
                        </motion.div>
                      )}

                      {/* Submit Button */}
                      <motion.button
                        data-hover
                        type="submit"
                        disabled={isSubmitting}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="font-mono"
                        style={{
                          width: '100%',
                          padding: '16px',
                          marginTop: 8,
                          background: isSubmitting ? 'rgba(0,255,65,0.5)' : '#00FF41',
                          border: 'none',
                          color: '#050505',
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: '0.2em',
                          cursor: isSubmitting ? 'not-allowed' : 'none',
                          transition: 'background 0.2s',
                        }}
                      >
                        {isSubmitting ? 'JOINING...' : '[ JOIN WAITLIST ]'}
                      </motion.button>
                    </div>
                  </form>

                  {/* Privacy Note */}
                  <p
                    className="font-mono"
                    style={{
                      fontSize: 9,
                      color: 'rgba(242,242,242,0.4)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginTop: 24,
                      textAlign: 'center',
                      lineHeight: 1.6,
                    }}
                  >
                    Your data is encrypted and will never be shared
                  </p>
                </>
              ) : (
                /* Success State */
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  style={{
                    textAlign: 'center',
                    padding: '40px 0',
                  }}
                >
                  <div
                    style={{
                      width: 60,
                      height: 60,
                      margin: '0 auto 24px',
                      border: '2px solid #00FF41',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#00FF41"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <h3
                    style={{
                      fontSize: 24,
                      fontWeight: 600,
                      marginBottom: 12,
                      color: '#00FF41',
                    }}
                  >
                    WELCOME TO BLANK
                  </h3>
                  <p
                    className="font-mono"
                    style={{
                      fontSize: 11,
                      color: 'rgba(242,242,242,0.6)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    You're on the list. Check your email.
                  </p>
                </motion.div>
              )}
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default WaitlistModal;
